import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { API } from 'constants';
import client from '../../../services/template-api';

import { VIEW_TYPES } from '../../../constants/viewTypes';
import { defaultErrorMessage } from '../../../constants/errorMessages';

import { useViewType } from '../../../hooks/useViewType';
import { useSwrRequestKeysToMutateFromContext } from '../../../hooks/useSwrRequestKeysToMutateFromContext';

import {
    mutateAllCachedRequest,
    mutateDataAfterDeletingItem,
    mutateListDataAfterUpdatingItem,
} from '../../../helpers/swrMutateUtils';
import { mergeTwoArraysRemovingDuplicates } from '../../../helpers/mergeTwoArraysRemovingDuplicates';

import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import { ListOption, ListOptionGroup } from '../../../design-system';

const PlaybookTemplateMoreOptionsPopup = ({
    id,
    is_live,
    requestKeysToMutate: _requestKeysToMutate,
    onIsLiveChange,
    onDelete,
    position,
    onClose,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { viewType, toggleViewType } = useViewType();

    const {
        requestKeysToMutate: { playbookTemplates: requestKeysFromContext },
    } = useSwrRequestKeysToMutateFromContext();

    const [optionLoading, setOptionLoading] = useState(null);
    const [errorAlert, setErrorAlert] = useState(null);

    const handleViewInClientSide = () => {
        if (viewType === VIEW_TYPES.admin) {
            toggleViewType();
        }
        navigate(`/library/playbook/${id}`);
        onClose();
    };

    const handleNavToEditPage = () => {
        navigate(`/templates/playbook/${id}/edit`, {
            state: { templatesLibraryLocation: location },
        });
    };

    const getRequestKeysToMutate = () => {
        return mergeTwoArraysRemovingDuplicates(requestKeysFromContext, _requestKeysToMutate);
    };

    const mutateLiveTemplatesRequestKeys = (requestKeysToMutate) => {
        const keysWithIsLiveFiltering = requestKeysToMutate.filter((key) => {
            const loweredKey = key?.toLowerCase() || '';
            return loweredKey.includes('is_live=true');
        });
        mutateAllCachedRequest({ requestKeysToMutate: keysWithIsLiveFiltering });
    };

    const handleIsLiveChange = async (is_live) => {
        try {
            setOptionLoading('changing_is_live');
            const { data } = await client.patch(API.ROUTES.template.playbookTemplate + id + '/', {
                is_live,
            });

            onIsLiveChange && onIsLiveChange(data);

            const requestKeysToMutate = getRequestKeysToMutate();
            mutateListDataAfterUpdatingItem({
                requestKeysToMutate,
                id,
                updatedPart: {
                    is_live: data.is_live,
                    updated_at: data.updated_at,
                },
            });

            mutateLiveTemplatesRequestKeys(requestKeysToMutate);

            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setOptionLoading(null);
        }
    };

    const handleDelete = async () => {
        try {
            setOptionLoading('delete');
            await client.delete(API.ROUTES.template.playbookTemplate + id + '/');

            onDelete && onDelete(id);

            const requestKeysToMutate = getRequestKeysToMutate();
            mutateDataAfterDeletingItem({ requestKeysToMutate, id });

            if (is_live) {
                mutateLiveTemplatesRequestKeys(requestKeysToMutate);
            }

            onClose();
        } catch (e) {
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            setOptionLoading(null);
        }
    };

    const containerClassName = classNames('absolute z-55', position || 'top-8 right-1');

    return (
        <div className={containerClassName} onClick={(e) => e.preventDefault()}>
            <ListOptionGroup
                fixedWidth={280}
                listOptions={
                    <>
                        <ListOption
                            leadingIconName="editLineIcon"
                            text="Edit Details"
                            onClick={handleNavToEditPage}
                        />
                        {is_live && (
                            <ListOption
                                leadingIconName="eyeOffIcon"
                                text="Hide"
                                isLoading={optionLoading === 'changing_is_live'}
                                onClick={() => handleIsLiveChange(false)}
                            />
                        )}
                        {!is_live && (
                            <ListOption
                                leadingIconName="eyeIcon"
                                text="Show"
                                isLoading={optionLoading === 'changing_is_live'}
                                onClick={() => handleIsLiveChange(true)}
                            />
                        )}
                        {is_live && (
                            <ListOption
                                leadingIconName="shareBoxLineIcon"
                                text="View in Client Side"
                                onClick={handleViewInClientSide}
                            />
                        )}
                        <ListOption
                            leadingIconName="deleteBin4LineIcon"
                            text="Delete"
                            textColor="#E95B69"
                            iconColor="#E95B69"
                            isLoading={optionLoading === 'delete'}
                            onClick={handleDelete}
                        />
                    </>
                }
                handleClose={onClose}
            />

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default PlaybookTemplateMoreOptionsPopup;
